import { useState, MouseEvent, ChangeEvent } from 'react';
import {
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Toolbar,
  Paper,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { visuallyHidden } from '@mui/utils';
import { AddUserInput, UserNode } from '../../../../../../graphql/generated/graphql';
import Avatar from '../../../../components/avatar';
import ContactsModal from '../../../../components/contacts/Modal';

interface Data {
  id: string
  avatar?: string;
  name: string;
  jobTitle: string;
  JobTitleId: string;
  email: string;
  phone: string;
}

function createData(
  id: string,
  avatar: string,
  name: string,
  jobTitle: string,
  JobTitleId: string,
  email: string,
  phone: string,
): Data {
  return {
    id,
    avatar,
    name,
    jobTitle,
    JobTitleId,
    email,
    phone,
  };
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'name',
    numeric: false,
    disablePadding: true,
    label: 'Name',
  },
  {
    id: 'jobTitle',
    numeric: false,
    disablePadding: false,
    label: 'Role',
  },
  {
    id: 'email',
    numeric: false,
    disablePadding: false,
    label: 'Email',
  },
  {
    id: 'phone',
    numeric: false,
    disablePadding: false,
    label: 'Phone Number',
  },
];

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (event: MouseEvent<unknown>, property: keyof Data) => void;
  onSelectAllClick: (event: ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  canEditing?: boolean;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const {
    onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort, canEditing
  } = props;
  const createSortHandler = (property: keyof Data) => (event: MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead
      sx={{
        boxShadow: 10,
      }}
    >
      <TableRow>
        {canEditing &&
          <TableCell padding="checkbox">
            <Checkbox
              color="primary"
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{
                'aria-label': 'select all desserts',
              }}
            />
          </TableCell>
        }
        {headCells.map((headCell, index) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{
              paddingLeft: !canEditing
                ? index === 0
                  ? '25px'
                  : '15px'
                : '0px'
            }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              sx={{
                fontWeight: 500,
                fontSize: 16,
              }}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

interface EnhancedTableToolbarProps {
  numSelected: number;
  deleteUsers: any;
  selectedUsers: any;
  setSelectedUsers: any;
  canEditing?: boolean;
}

interface UserNodeProps {
  contacts: [UserNode],
  deleteUsersProject: any,
  openModal: any,
  handleCloseModal: any,
  handleOpenModal: any,
  canEditing?: boolean
}

const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
  const {
    numSelected, deleteUsers, selectedUsers, setSelectedUsers, canEditing
  } = props;

  return (
    <Toolbar
      sx={{
        display: 'flex',
        justifyContent: 'end',
      }}
    >
      {numSelected > 0 && canEditing && (
        <Tooltip title="Delete">
          <IconButton onClick={() => {
            deleteUsers(selectedUsers);
            setSelectedUsers([]);
          }}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );
};

const ContactsTable = (props: UserNodeProps) => {
  const {
    contacts, deleteUsersProject, openModal, handleCloseModal, handleOpenModal, canEditing
  } = props;
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof Data>('name');
  const [rowInfo, setRowInfo] = useState<any>();
  const [selected, setSelected] = useState<readonly AddUserInput[]>([]);

  const rows: any[] = [];

  contacts.forEach((element) => {
    rows.push(
      createData(
        element.id || '',
        element.url_avatar || '',
        element.full_name || '',
        element.job_title?.name || '',
        element.job_title?.id || '',
        element.email || '',
        element.phone || '',
      ),
    )
  });

  const handleRequestSort = (
    event: MouseEvent<unknown>,
    property: keyof Data,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n) => ({ email: n.email }));
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: MouseEvent<unknown>, email: string) => {
    const selectedIndex = selected.map((e) => e.email).indexOf(email);
    let newSelected: readonly AddUserInput[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, { email });
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const isSelected = (email: string) => selected.map((e) => e.email).indexOf(email) !== -1;

  return (
    <Box sx={{ width: '100%' }}>
      <EnhancedTableToolbar
        numSelected={selected.length}
        selectedUsers={selected}
        deleteUsers={deleteUsersProject}
        setSelectedUsers={setSelected}
        canEditing={canEditing}
      />
      <TableContainer component={Paper}>
        <Table
          sx={{ minWidth: 750 }}
          aria-labelledby="tableTitle"
        >
          <EnhancedTableHead
            numSelected={selected.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={rows.length}
            canEditing={canEditing}
          />
          <TableBody
            sx={{
              backgroundColor: '#E5E5E5',
            }}
          >
            {stableSort(rows, getComparator(order, orderBy))
              .map((row, index) => {
                const isItemSelected = isSelected(row.email as string);
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.id}
                    selected={isItemSelected}
                  >
                    {canEditing &&
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          onClick={(event) => handleClick(event, row.email as string)}
                          inputProps={{
                            'aria-labelledby': labelId,
                          }}
                        />
                      </TableCell>
                    }
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      padding="none"
                      sx={{
                        paddingLeft: canEditing ? '0px' : '20px'
                      }}
                    >
                      <Grid
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Avatar
                          url={row.avatar as string | null}
                          size={50}
                          style={{
                            marginRight: '15px'
                          }} />
                        {row.name}
                      </Grid>
                    </TableCell>
                    <TableCell align="left">{row.jobTitle}</TableCell>
                    <TableCell align="left">{row.email}</TableCell>
                    <TableCell align="left">{row.phone}</TableCell>
                    <TableCell align="right">
                      {canEditing && <>
                        <IconButton
                          onClick={() => {
                            deleteUsersProject([{ email: row.email }])
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            handleOpenModal();
                            setRowInfo(row);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </>
                      }
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <ContactsModal open={openModal} close={handleCloseModal} item={rowInfo} />
    </Box>
  );
}

export default ContactsTable;
